'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Flame } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { toast } from 'sonner';
import { useAppContext } from '@/shared/contexts/app';
import { trackDailyCheckin } from '@/shared/lib/analytics';

interface CheckinStatus {
  hasCheckedInToday: boolean;
  consecutiveDays: number;
  tomorrowRewardCredits: number;
}

export function CheckinButton() {
  const { isCheckSign, user, setIsShowSignModal } = useAppContext();
  const t = useTranslations('common.checkin-button');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState<CheckinStatus | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check if user is logged in
  const isLoggedIn = !!user;

  const handleOpenDialog = async () => {
    if (!isLoggedIn) {
      // 未登录，显示登录弹窗
      setIsShowSignModal(true);
      return;
    }

    setIsOpen(true);

    // 获取签到状态
    try {
      const response = await fetch('/api/checkin/claim?action=status');
      if (response.ok) {
        const status = await response.json();
        setCheckinStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch checkin status:', error);
    }
  };

  const handleCheckin = async () => {
    if (!isLoggedIn) {
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/checkin/claim', {
        method: 'POST',
      });

      if (!response.ok) {
        const error = await response.json();

        // 埋点：签到失败
        trackDailyCheckin({
          credits_earned: 0,
          consecutive_days: checkinStatus?.consecutiveDays || 0,
          success: false,
          error_message: error.error || t('checkin-failed'),
        });

        throw new Error(error.error || t('checkin-failed'));
      }

      const result = await response.json();
      const dayKey = Math.min(result.consecutiveDays, 5);

      // 埋点：签到成功
      trackDailyCheckin({
        credits_earned: result.rewardCredits,
        consecutive_days: result.consecutiveDays,
        success: true,
      });

      // 使用新的成功文案
      toast.success(t(`day_${dayKey}.success_title`), {
        description: t(`day_${dayKey}.success_reward`),
        duration: 3000,
      });

      // 更新状态
      setCheckinStatus({
        hasCheckedInToday: true,
        consecutiveDays: result.consecutiveDays,
        tomorrowRewardCredits: Math.min(result.consecutiveDays + 1, 5),
      });

      // 延迟关闭对话框
      setTimeout(() => {
        setIsOpen(false);
      }, 2000);
    } catch (error: any) {
      toast.error(t('checkin-failed'), {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpenDialog}
        disabled={!mounted || isCheckSign}
        className="flex items-center gap-2 relative"
      >
        <div className="relative">
          <Flame className="h-4 w-4 text-orange-500" />
          {mounted && isLoggedIn && checkinStatus && checkinStatus.consecutiveDays > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {checkinStatus.consecutiveDays > 9 ? '9+' : checkinStatus.consecutiveDays}
            </span>
          )}
        </div>
        <span className="hidden sm:inline text-sm">{t('free-credits')}</span>
      </Button>

      {/* Checkin Dialog */}
      {mounted && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base font-bold flex items-center gap-2 pr-8">
                <Flame className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <span className="break-words leading-tight">{t('title')}</span>
              </DialogTitle>
              <DialogDescription className="text-xs break-words">
                {t('subtitle')}
              </DialogDescription>
            </DialogHeader>

            {checkinStatus ? (
              <div className="space-y-3">
                {/* 今日奖励卡片 - 未签到时显示 */}
                {!checkinStatus.hasCheckedInToday && (
                  <div className="rounded-lg border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-3 space-y-2">
                    <h3 className="text-sm font-semibold text-foreground break-words leading-tight">
                      {t(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_title`)}
                    </h3>
                    <div className="space-y-1.5">
                      <p className="text-base font-bold text-primary break-words leading-tight">
                        {t(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_main`)}
                      </p>
                      <p className="text-xs text-muted-foreground break-words leading-snug">
                        {t(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.reward_sub`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* 签到成功卡片 - 已签到时显示 */}
                {checkinStatus.hasCheckedInToday && (
                  <div className="rounded-lg border-2 border-green-500/30 bg-gradient-to-br from-green-50 via-green-100/50 to-green-50 dark:from-green-950/20 dark:via-green-900/30 dark:to-green-950/20 p-3 space-y-2">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <span className="text-xl flex-shrink-0">🎉</span>
                      <span className="break-words leading-tight">{t(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_title`)}</span>
                    </h3>
                    <div className="space-y-1.5">
                      <p className="text-base font-bold text-green-600 dark:text-green-400 break-words leading-tight">
                        {t(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_reward`)}
                      </p>
                      <p className="text-xs text-muted-foreground break-words leading-snug">
                        {t(`day_${Math.min(checkinStatus.consecutiveDays, 5)}.success_available`)}
                      </p>
                    </div>
                  </div>
                )}

                {/* 明日预告 - 仅在未达到第5天时显示 */}
                {!checkinStatus.hasCheckedInToday && checkinStatus.consecutiveDays < 4 && (
                  <div className="rounded-lg border bg-muted/50 p-2.5 space-y-1">
                    <h4 className="text-xs font-medium text-muted-foreground break-words leading-tight">
                      {t(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.tomorrow_preview`)}
                    </h4>
                    <p className="text-xs font-semibold text-foreground break-words leading-tight">
                      {t(`day_${Math.min(checkinStatus.consecutiveDays + 2, 5)}.reward_main`)}
                    </p>
                  </div>
                )}

                {/* 连续天数统计 */}
                <div className="flex items-center justify-center gap-4 py-2">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {checkinStatus.consecutiveDays}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('consecutive-days')}
                    </div>
                  </div>
                  {checkinStatus.hasCheckedInToday && (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-500">✓</div>
                      <div className="text-xs text-muted-foreground">
                        {t('status')}
                      </div>
                    </div>
                  )}
                </div>

                {/* 签到按钮 */}
                <Button
                  onClick={handleCheckin}
                  disabled={checkinStatus.hasCheckedInToday || isLoading}
                  size="lg"
                  className="w-full text-sm font-semibold break-words whitespace-normal h-auto py-2.5 px-4 leading-tight"
                >
                  {checkinStatus.hasCheckedInToday
                    ? t('button-checked')
                    : t(`day_${Math.min(checkinStatus.consecutiveDays + 1, 5)}.cta`)}
                </Button>

                {/* 查看详情链接 */}
                <div className="text-center">
                  <Link
                    href="/settings/checkin"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('view-details')} →
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-sm text-muted-foreground">
                  {t('loading')}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
